import React, { useMemo, useState } from 'react';
import rules from './pediatricDrugRules.json';
import './drug-calculator.css';

const LB_PER_KG = 2.2046226218;

function fmt(value, digits = 2) {
  if (!Number.isFinite(value)) return '—';
  return Number(value).toFixed(digits).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
}

function fmtMl(value) {
  return Number.isFinite(value) ? `${Number(value).toFixed(2)} mL` : '—';
}

function eligibility(option, weightKg, ageYears) {
  if (!Number.isFinite(weightKg) || weightKg <= 0) return 'Enter a valid patient weight.';
  if (weightKg > 36) return 'Weight exceeds 36 kg. Per Pediatric Parameters 9016.15, use adult dosages for a known pediatric patient above 36 kg; switch to Adult mode.';

  const hasAge = Number.isFinite(ageYears) && ageYears >= 0;
  if (option.ageOrWeight) {
    const ageOk = option.ageMinYears == null || (hasAge && ageYears >= option.ageMinYears);
    const weightOk = option.weightMinKg == null || weightKg >= option.weightMinKg;
    if (!ageOk && !weightOk) return `This option requires age ≥ ${option.ageMinYears} years and/or weight ≥ ${option.weightMinKg} kg.`;
    return '';
  }

  if (option.ageMinYears != null) {
    if (!hasAge) return `Enter age to verify the ≥ ${option.ageMinYears}-year requirement.`;
    if (ageYears < option.ageMinYears) return `This option requires age ≥ ${option.ageMinYears} years.`;
  }
  if (option.ageMaxYears != null) {
    if (!hasAge) return `Enter age to verify the age requirement for this option.`;
    if (ageYears > option.ageMaxYears) return `This option is limited to age ≤ ${fmt(option.ageMaxYears, 1)} years.`;
  }
  if (option.ageMinMonths != null) {
    if (!hasAge) return 'Enter age to verify the minimum-age requirement.';
    if (ageYears * 12 <= option.ageMinMonths) return 'This option requires the patient to be older than 1 month.';
  }
  if (option.weightMinKg != null && weightKg < option.weightMinKg) return `This option requires weight ≥ ${option.weightMinKg} kg.`;
  return '';
}

export function calculatePediatricDose(option, weightKg, concentration) {
  if (!option || !Number.isFinite(weightKg) || weightKg <= 0) return null;

  if (option.doseRange) {
    const [minDose, maxDose] = option.doseRange;
    return {
      type: 'range',
      doseMin: minDose,
      doseMax: maxDose,
      volumeMin: concentration ? minDose / concentration.amountPerMl : null,
      volumeMax: concentration ? maxDose / concentration.amountPerMl : null,
    };
  }

  if (option.weightBands) {
    const band = option.weightBands.find(item => weightKg >= item.min && weightKg <= item.max);
    if (!band) return null;
    return {
      type: 'dose',
      dose: band.dose,
      uncappedDose: band.dose,
      capped: false,
      volume: concentration ? band.dose / concentration.amountPerMl : null,
      formula: `Weight band ${band.min}-${band.max === 999 ? '+' : band.max} kg`,
    };
  }

  if (option.volumePerKg != null) {
    return {
      type: 'volume',
      volume: weightKg * option.volumePerKg,
      formula: `${option.volumePerKg} mL/kg`,
    };
  }

  if (option.fixedDose != null) {
    return {
      type: 'dose',
      dose: option.fixedDose,
      uncappedDose: option.fixedDose,
      capped: false,
      volume: concentration ? option.fixedDose / concentration.amountPerMl : null,
      formula: 'Fixed protocol dose',
    };
  }

  if (option.dosePerKg != null) {
    const uncappedDose = weightKg * option.dosePerKg;
    let dose = uncappedDose;
    if (option.minDose != null) dose = Math.max(dose, option.minDose);
    if (option.maxDose != null) dose = Math.min(dose, option.maxDose);
    return {
      type: 'dose',
      dose,
      uncappedDose,
      capped: dose !== uncappedDose,
      volume: concentration ? dose / concentration.amountPerMl : null,
      formula: `${option.dosePerKg} ${option.doseUnit}/kg`,
    };
  }

  return null;
}

function Result({ group, option, weightKg, ageYears }) {
  const concentration = option.concentrationId ? rules.concentrations[option.concentrationId] : null;
  const restriction = eligibility(option, weightKg, ageYears);
  const result = restriction ? null : calculatePediatricDose(option, weightKg, concentration);

  if (restriction) return <div className="drugcalc-review-lock"><strong>Check patient eligibility.</strong><span>{restriction}</span></div>;
  if (!result) return <div className="drugcalc-empty">This selection does not have a calculation result for the entered patient.</div>;

  const cumulativeDose = option.cumulativePerKg != null
    ? Math.min(weightKg * option.cumulativePerKg, option.cumulativeMax ?? Infinity)
    : null;
  const cumulativeVolume = cumulativeDose != null && concentration ? cumulativeDose / concentration.amountPerMl : null;

  return (
    <div className="drugcalc-stack">
      <section className="drugcalc-card drugcalc-card--hero">
        <div className="drugcalc-result-head">
          <div><span className="drugcalc-kicker">PEDIATRIC · {option.label.toUpperCase()}</span><h3>{group.label}</h3></div>
          <span className="drugcalc-protocol-pill">{group.protocol}</span>
        </div>
        <div className="drugcalc-weight-row"><span>{fmt(weightKg, 1)} kg</span><span>·</span><strong>{fmt(weightKg * LB_PER_KG, 1)} lb</strong></div>

        {result.type === 'range' ? (
          <div className="drugcalc-big-result">
            <div><small>PROTOCOL RANGE</small><strong>{fmt(result.doseMin)}-{fmt(result.doseMax)} {option.doseUnit}</strong></div>
            <div className="drugcalc-equals">=</div>
            <div><small>DRAW / GIVE</small><strong>{concentration ? `${fmtMl(result.volumeMin)}-${fmtMl(result.volumeMax)}` : 'Dose only'}</strong></div>
          </div>
        ) : result.type === 'volume' ? (
          <div className="drugcalc-result-grid">
            <div><small>PROTOCOL FORMULA</small><strong>{result.formula}</strong></div>
            <div className="drugcalc-volume-result"><small>GIVE</small><strong>{fmtMl(result.volume)}</strong><span>{option.label}</span></div>
          </div>
        ) : (
          <div className="drugcalc-result-grid">
            <div><small>PROTOCOL FORMULA</small><strong>{result.formula}</strong></div>
            <div><small>CALCULATED DOSE</small><strong>{fmt(result.dose)} {option.doseUnit}</strong>{result.capped ? <span className="drugcalc-cap">Protocol min/max applied</span> : null}</div>
            <div className="drugcalc-volume-result"><small>{concentration ? 'DRAW / GIVE' : 'DOSE'}</small><strong>{concentration ? fmtMl(result.volume) : `${fmt(result.dose)} ${option.doseUnit}`}</strong><span>{concentration?.display || 'No carried concentration configured — dose only'}</span></div>
          </div>
        )}
      </section>

      {option.splitNares && result.volume != null ? <section className="drugcalc-card"><span className="drugcalc-kicker">INTRANASAL DELIVERY</span><p><strong>{fmtMl(result.volume / 2)} per nostril</strong> when this dose is administered IN through an atomization device.</p></section> : null}

      {cumulativeDose != null ? <section className="drugcalc-card"><span className="drugcalc-kicker">CUMULATIVE LIMIT</span><div className="drugcalc-limit-grid"><div><small>THIS WEIGHT</small><strong>{fmt(cumulativeDose)} {option.doseUnit}</strong></div><div><small>AT CARRIED CONCENTRATION</small><strong>{fmtMl(cumulativeVolume)}</strong></div></div></section> : null}

      <section className="drugcalc-card drugcalc-card--compact">
        <div className="drugcalc-protocol-ref"><strong>{group.protocol} · {group.label}</strong><span>{option.route} · {option.repeat}</span></div>
        <p>{option.notes}</p>
      </section>
    </div>
  );
}

export default function PediatricDrugCalculator({ onOpenProtocols }) {
  const [groupId, setGroupId] = useState(rules.groups[0].id);
  const group = useMemo(() => rules.groups.find(item => item.id === groupId) || rules.groups[0], [groupId]);
  const [optionId, setOptionId] = useState(rules.groups[0].options[0].id);
  const option = useMemo(() => group.options.find(item => item.id === optionId) || group.options[0], [group, optionId]);
  const [weightUnit, setWeightUnit] = useState('kg');
  const [weight, setWeight] = useState('');
  const [ageYearsInput, setAgeYearsInput] = useState('');

  const rawWeight = Number(weight);
  const weightKg = Number.isFinite(rawWeight) && rawWeight > 0 ? (weightUnit === 'lb' ? rawWeight / LB_PER_KG : rawWeight) : NaN;
  const ageYears = ageYearsInput === '' ? NaN : Number(ageYearsInput);
  const validWeight = Number.isFinite(weightKg) && weightKg > 0 && weightKg <= 100;
  const reviewLocked = Date.now() >= Date.parse(`${rules.reviewBefore}T00:00:00-07:00`);

  const chooseGroup = nextId => {
    setGroupId(nextId);
    const next = rules.groups.find(item => item.id === nextId);
    if (next?.options?.length) setOptionId(next.options[0].id);
  };

  return (
    <div className="drugcalc-root">
      <div className="drugcalc-meta">
        <div><span className="drugcalc-kicker">OFFLINE · PEDIATRIC FIELD TOOL</span><h3>Pediatric Drug Calculator</h3><p>Current Sacramento County 9000-series medication calculations using configured Line Medic carried concentrations.</p></div>
        <span className="drugcalc-offline">NO NETWORK REQUIRED</span>
      </div>

      <div className="drugcalc-review-lock" style={{ marginBottom: 14 }}>
        <strong>Pediatric Parameters {rules.parameters.protocol}</strong>
        <span>{rules.parameters.summary}</span>
      </div>

      {reviewLocked ? (
        <div className="drugcalc-review-lock"><strong>Protocol review required before pediatric calculator use.</strong><span>Sacramento County has approved pediatric protocol changes effective November 1, 2026. Use the official Protocols library until this ruleset is re-verified against the effective documents.</span>{onOpenProtocols ? <button type="button" onClick={onOpenProtocols}>Open Protocols</button> : null}</div>
      ) : (
        <>
          <section className="drugcalc-controls">
            <label><span>Protocol / indication</span><select value={groupId} onChange={event => chooseGroup(event.target.value)}>{rules.groups.map(item => <option key={item.id} value={item.id}>{item.protocol} · {item.label}</option>)}</select></label>
            <label><span>Medication / route</span><select value={option.id} onChange={event => setOptionId(event.target.value)}>{group.options.map(item => <option key={item.id} value={item.id}>{item.label}</option>)}</select></label>
            <label><span>Patient weight</span><div style={{ display: 'flex', gap: 8 }}><input type="number" inputMode="decimal" min="0.1" max={weightUnit === 'kg' ? '100' : '220'} step="0.1" value={weight} onChange={event => setWeight(event.target.value)} placeholder={`Weight in ${weightUnit}`} /><select value={weightUnit} onChange={event => setWeightUnit(event.target.value)}><option value="kg">kg</option><option value="lb">lb</option></select></div>{validWeight ? <small>{fmt(weightKg, 1)} kg · {fmt(weightKg * LB_PER_KG, 1)} lb</small> : <small>Enter measured/estimated pediatric weight</small>}</label>
            <label><span>Age in years</span><input type="number" inputMode="decimal" min="0" max="18" step="0.1" value={ageYearsInput} onChange={event => setAgeYearsInput(event.target.value)} placeholder="Optional unless rule is age-limited" /><small>Use decimals for infants (e.g. 0.5 = 6 months)</small></label>
          </section>

          {validWeight ? <Result group={group} option={option} weightKg={weightKg} ageYears={ageYears} /> : <div className="drugcalc-empty">Choose the protocol and medication, then enter the patient’s weight.</div>}
        </>
      )}

      <div className="drugcalc-footer-note">
        <strong>Field calculation aid — confirm the official Sacramento County protocol, indication, route, concentration, and medication label before administration.</strong>
        <span>Current rules verified {rules.verifiedAt}. Mandatory review before {rules.reviewBefore}. Morphine and diazepam are intentionally excluded from the configured carried set; ketorolac is dose-only because no carried concentration is configured.</span>
      </div>
    </div>
  );
}
